import { writable, get } from "svelte/store";

/**
 * Store for the bootstrap token most-recently provided by the host application.
 *
 * Only used during the `/auth/exchange` handshake (see `authExchange.ts`).
 * Once the handshake completes the BFF session cookie carries auth state and
 * the bootstrap token is not consulted again — except when a 401 forces a
 * re-handshake (see `requestFreshBootstrapToken` below).
 */
export const bootstrapToken = writable<string | null>(null);

/**
 * Callback registered by the custom element to dispatch the
 * `mfe-token-refresh-needed` event to the host page.
 */
export const tokenRefreshCallback = writable<(() => void) | null>(null);

const REFRESH_TIMEOUT_MS = 10_000;

interface PendingRefresh {
  resolve: (token: string) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

let pending: PendingRefresh | null = null;

bootstrapToken.subscribe((value) => {
  if (value && pending) {
    const { resolve, timer } = pending;
    clearTimeout(timer);
    pending = null;
    resolve(value);
  }
});

/**
 * Asks the host page for a fresh bootstrap token by dispatching
 * `mfe-token-refresh-needed`, then resolves the next time the host writes a
 * new value via the `bootstrapToken` JS property setter (which forwards into
 * this store).
 *
 * Rejects after `REFRESH_TIMEOUT_MS` if no fresh token arrives, or
 * immediately if no host callback is registered.
 *
 * Concurrent callers share the same in-flight promise so a burst of 401s
 * triggers only one refresh round-trip.
 */
export function requestFreshBootstrapToken(): Promise<string> {
  const callback = get(tokenRefreshCallback);
  if (!callback) {
    return Promise.reject(
      new Error("No host callback registered for bootstrap token refresh"),
    );
  }

  if (pending) {
    return new Promise<string>((resolve, reject) => {
      const previous = pending!;
      pending = {
        resolve: (token) => {
          previous.resolve(token);
          resolve(token);
        },
        reject: (err) => {
          previous.reject(err);
          reject(err);
        },
        timer: previous.timer,
      };
    });
  }

  return new Promise<string>((resolve, reject) => {
    const timer = setTimeout(() => {
      const err = new Error(
        `Timed out after ${REFRESH_TIMEOUT_MS} ms waiting for fresh bootstrap token`,
      );
      const p = pending;
      pending = null;
      p?.reject(err);
    }, REFRESH_TIMEOUT_MS);
    pending = { resolve, reject, timer };
    callback();
  });
}
